// Phase 5 — Parts Assist Intake Modal.
// Confirms a vendor-captured part payload before persisting via the
// CANONICAL useCreatePartsLine() mutation. NEVER writes through any other path.
//
// Phase 5C UX hardening:
//   • IntakePricingPreview replaces the simple PartsPricingHint
//   • Capture confidence badge in header
//   • 3-tier validation row (blocking / warning / info)
//   • Smarter defaults: qty fallback to 1, last-used job per ticket cached
//     in sessionStorage
//   • Job context line under the picker (book minutes from ticket_jobs)
//   • Upgraded duplicate detection (exact PN OR fuzzy description)
//   • Recent captures chip strip (localStorage only)
//
// Contracts enforced here:
//   • Phase 3.5 pricing — vendor cost lands in unit_cost only; suggested sell
//     is derived by the existing engine. No vendor price ever overrides OptiBay.
//   • Phase 4 job assignment — uses the same JobOption[] shape the existing
//     PartsLineFormDialog uses, populated from real ticket_jobs (no temp ids).
//   • Validation: blocks qty <= 0 / invalid cost / missing description; warns
//     on missing part_number, missing cost, duplicates; never auto-merges.

import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
// DTC-K: route the parts-capture `captured_at` fallback through the
// canonical clock so the parts-flow audit timestamp is deterministic
// under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ShoppingBag, Info, AlertCircle } from 'lucide-react';
import { IntakePricingPreview } from './IntakePricingPreview';
import { CaptureConfidenceBadge, inferCaptureConfidence } from './CaptureConfidenceBadge';
import { RecentCaptures } from './RecentCaptures';
import { findDuplicate } from '@/lib/parts-assist/duplicateDetection';
import {
  loadRecentCaptures,
  pushRecentCapture,
  clearRecentCaptures,
  type RecentCapture,
} from '@/lib/parts-assist/recentCapturesStore';
import type { JobOption } from '@/components/tickets/parts/PartsLineFormDialog';
import type { PartsLine, PartsLineInsert } from '@/hooks/parts/usePartsLines';
import type { PartsAssistCapturePayload } from '@/lib/parts-assist/messageContract';

const SESSION_KEY = (ticketId: string) => `pa:lastJob:${ticketId}`;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  ticketId: string;
  jobOptions: JobOption[];
  /** Existing parts on this ticket — used for soft duplicate detection. */
  existingLines: PartsLine[];
  /** Captured payload from the extension (or manual paste). */
  capture: PartsAssistCapturePayload | null;
  onSubmit: (payload: PartsLineInsert) => Promise<void> | void;
}

export function PartsAssistIntakeModal({
  open,
  onOpenChange,
  shopId,
  ticketId,
  jobOptions,
  existingLines,
  capture,
  onSubmit,
}: Props) {
  const [description, setDescription] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitCost, setUnitCost] = useState('');
  const [coreCharge, setCoreCharge] = useState('0');
  const [availability, setAvailability] = useState('');
  const [notes, setNotes] = useState('');
  const [jobId, setJobId] = useState('__ticket__');
  const [submitting, setSubmitting] = useState(false);
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [recent, setRecent] = useState<RecentCapture[]>([]);

  // Hydrate from capture every time the modal opens with a new payload.
  useEffect(() => {
    if (!open) return;
    setRecent(loadRecentCaptures());
    if (!capture) return;
    setDescription(capture.description ?? '');
    setPartNumber(capture.part_number ?? '');
    // Smarter default: qty falls back to 1 when missing/invalid.
    const qInit =
      capture.quantity != null && Number.isFinite(Number(capture.quantity)) && Number(capture.quantity) > 0
        ? String(capture.quantity)
        : '1';
    setQuantity(qInit);
    setUnitCost(capture.unit_cost != null ? String(capture.unit_cost) : '');
    setCoreCharge(capture.core_charge != null ? String(capture.core_charge) : '0');
    setAvailability(capture.availability ?? '');
    setNotes(capture.notes ?? '');
    setAllowDuplicate(false);

    // Smarter default: pre-select last-used job for this ticket (session only).
    let initialJob = '__ticket__';
    try {
      const stored = sessionStorage.getItem(SESSION_KEY(ticketId));
      if (stored && (stored === '__ticket__' || jobOptions.some((j) => j.job_id === stored))) {
        initialJob = stored;
      }
    } catch {
      /* sessionStorage may be disabled */
    }
    setJobId(initialJob);
  }, [open, capture, ticketId, jobOptions]);

  const qtyNum = Number(quantity);
  const costNum = unitCost === '' ? null : Number(unitCost);
  const coreNum = Number(coreCharge || 0);

  const qtyInvalid = !Number.isFinite(qtyNum) || qtyNum <= 0;
  const costInvalid = costNum != null && (!Number.isFinite(costNum) || costNum < 0);
  const coreInvalid = !Number.isFinite(coreNum) || coreNum < 0;
  const descMissing = !description.trim();

  const missingCostWarning = costNum == null || costNum === 0;
  const missingPartNumberWarning = !partNumber.trim();

  // Phase 5C: confidence header indicator (derived).
  const confidence = useMemo(
    () =>
      inferCaptureConfidence({
        source_vendor: capture?.source_vendor,
        unit_cost: costNum,
        part_number: partNumber.trim() || null,
        explicit: (capture?.capture_confidence ?? null) as any,
      }),
    [capture, costNum, partNumber],
  );

  // Phase 5C: stronger duplicate detection (exact PN OR fuzzy desc).
  const duplicate = useMemo(
    () =>
      findDuplicate(
        existingLines.map((l) => ({
          id: l.id,
          description: l.description,
          vendor_part_number: l.vendor_part_number,
          job_id: l.job_id,
        })),
        {
          description,
          part_number: partNumber.trim() || null,
          job_id: jobId === '__ticket__' ? null : jobId,
        },
      ),
    [existingLines, description, partNumber, jobId],
  );
  const duplicateLine = duplicate
    ? existingLines.find((l) => l.id === duplicate.match.id) ?? null
    : null;

  const blocked =
    qtyInvalid || costInvalid || coreInvalid || descMissing ||
    (!!duplicate && !allowDuplicate);

  const selectedJob = jobOptions.find((j) => j.job_id === jobId) ?? null;

  const applyRecent = (entry: RecentCapture) => {
    setDescription(entry.description);
    if (entry.part_number) setPartNumber(entry.part_number);
    if (entry.unit_cost != null) setUnitCost(String(entry.unit_cost));
    setAllowDuplicate(false);
  };

  const handleClearRecent = () => {
    clearRecentCaptures();
    setRecent([]);
  };

  const handleReviewExisting = () => {
    if (!duplicateLine) return;
    onOpenChange(false);
    // Fire a window event the Parts panel can listen to and scroll/highlight.
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('optibay:parts-assist:review-line', {
          detail: { lineId: duplicateLine.id },
        }),
      );
    }, 0);
  };

  const handleSubmit = async () => {
    if (!capture || blocked) return;
    setSubmitting(true);
    try {
      const resolvedJobId = jobId === '__ticket__' ? null : jobId;
      const capturedAtISO = capture.captured_at ?? clock.now().toISOString();

      // Persist last-used job for this ticket (session only).
      try {
        sessionStorage.setItem(SESSION_KEY(ticketId), jobId);
      } catch {
        /* ignore */
      }

      const payload: PartsLineInsert = {
        shop_id: shopId,
        ticket_id: ticketId,
        job_id: resolvedJobId,
        vendor_name: capture.source_label ?? capture.source_vendor,
        vendor_part_number: partNumber.trim() || null,
        description: description.trim(),
        category: null,
        quantity: qtyNum,
        unit_cost: costNum,
        // Phase 3.5: leave actual_sell_* unset → engine derives suggested sell.
        // pricing_source 'imported' marks it as vendor-originated for analytics.
        pricing_source: 'imported',
        pricing_method: 'imported',
        manual_price_locked: false,
        taxable: true,
        status: 'needed',
        availability_text: availability.trim() || null,
        eta_text: null,
        core_charge: coreNum,
        source_type: 'parts_assist',
        source_reference: { vendor: capture.source_vendor, external_id: capture.external_id ?? null },
        notes: notes.trim() || null,
        // Phase 5 vendor-source columns.
        ...({
          source_vendor: capture.source_vendor,
          source_captured_at: capturedAtISO,
          source_availability: capture.availability ?? null,
          source_external_id: capture.external_id ?? null,
          source_payload: {
            ...(capture as unknown as Record<string, unknown>),
            capture_confidence: capture.capture_confidence ?? confidence,
          },
        } as any),
      };

      await onSubmit(payload);

      // Push to local recent-captures (browser only, no DB).
      pushRecentCapture({
        description: description.trim(),
        part_number: partNumber.trim() || null,
        unit_cost: costNum,
        source_vendor: capture.source_vendor,
        source_label: capture.source_label ?? null,
        captured_at: capturedAtISO,
      });

      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (!capture) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <ShoppingBag className="h-4 w-4" />
            Add captured part
            <Badge variant="secondary" className="ml-2 text-[10px]">
              {capture.source_label ?? capture.source_vendor}
            </Badge>
            <CaptureConfidenceBadge confidence={confidence} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {recent.length > 0 && (
            <RecentCaptures items={recent} onPick={applyRecent} onClear={handleClearRecent} />
          )}

          <div className="space-y-1.5">
            <Label htmlFor="pa-description">Description *</Label>
            <Input
              id="pa-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              className={descMissing ? 'border-destructive focus-visible:ring-destructive' : undefined}
            />
            {descMissing && (
              <p className="text-[11px] text-destructive">Description is required.</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="pa-partnum">Part #</Label>
              <Input
                id="pa-partnum"
                value={partNumber}
                onChange={(e) => setPartNumber(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pa-qty">Qty *</Label>
              <Input
                id="pa-qty"
                type="number"
                min="0"
                step="0.5"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className={qtyInvalid ? 'border-destructive focus-visible:ring-destructive' : undefined}
              />
              {qtyInvalid && (
                <p className="text-[11px] text-destructive">Qty must be greater than 0.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pa-core">Core</Label>
              <Input
                id="pa-core"
                type="number"
                min="0"
                step="0.01"
                value={coreCharge}
                onChange={(e) => setCoreCharge(e.target.value)}
              />
              {coreInvalid && (
                <p className="text-[11px] text-destructive">Invalid core charge.</p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pa-cost">Unit cost</Label>
            <Input
              id="pa-cost"
              type="number"
              min="0"
              step="0.01"
              value={unitCost}
              onChange={(e) => setUnitCost(e.target.value)}
              placeholder="leave blank if not provided by vendor"
              className={costInvalid ? 'border-destructive focus-visible:ring-destructive' : undefined}
            />
            {costInvalid && (
              <p className="text-[11px] text-destructive">Invalid unit cost.</p>
            )}
          </div>

          <IntakePricingPreview
            qty={qtyInvalid ? 1 : qtyNum}
            unitCost={costNum}
            category={null}
          />

          <div className="space-y-1.5">
            <Label htmlFor="pa-availability">Availability (from vendor)</Label>
            <Input
              id="pa-availability"
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
              placeholder="e.g. In stock, 2 hubs"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Attach to</Label>
            <Select value={jobId} onValueChange={setJobId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__ticket__">Whole ticket</SelectItem>
                {jobOptions.map((j) => (
                  <SelectItem key={j.job_id} value={j.job_id}>
                    {j.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedJob && (
              <p className="text-[11px] text-muted-foreground">
                Attaching to <span className="font-medium text-foreground">{selectedJob.name}</span>
                {selectedJob.standardMinutes != null && selectedJob.standardMinutes > 0 && (
                  <> · {(selectedJob.standardMinutes / 60).toFixed(1)} hrs (book)</>
                )}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pa-notes">Notes (internal)</Label>
            <Textarea
              id="pa-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* 3-tier validation row */}
          <div className="space-y-1.5">
            {duplicate && duplicateLine && (
              <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs space-y-2">
                <div className="flex items-start gap-2 font-medium">
                  <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5" />
                  <div>
                    <div>
                      Possible duplicate {duplicate.reason === 'part_number' ? '(same part #)' : '(similar description)'}
                    </div>
                    <div className="text-[11px] font-normal text-muted-foreground mt-0.5">
                      {duplicateLine.description}
                      {duplicateLine.vendor_part_number && ` · ${duplicateLine.vendor_part_number}`}
                      {duplicateLine.job_id ? ' · same job' : ' · ticket-level'}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px]"
                    onClick={() => setAllowDuplicate(true)}
                    disabled={allowDuplicate}
                  >
                    {allowDuplicate ? '✓ Will add anyway' : 'Add anyway'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-[11px]"
                    onClick={handleReviewExisting}
                  >
                    Review existing
                  </Button>
                </div>
              </div>
            )}

            {missingPartNumberWarning && <ValidationRow tier="warn" text="No vendor part number — saving anyway." />}
            {missingCostWarning && (
              <ValidationRow
                tier="warn"
                text="No vendor cost captured — Suggested price cannot be calculated until cost is set."
              />
            )}
            {confidence === 'manual' && (
              <ValidationRow tier="info" text="Manual entry — verify all fields before saving." />
            )}
            {confidence === 'partial' && capture.source_vendor === 'generic' && (
              <ValidationRow tier="info" text="Generic adapter — only description was auto-captured." />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || blocked}>
            Add to ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ValidationRow({ tier, text }: { tier: 'block' | 'warn' | 'info'; text: string }) {
  const cfg = {
    block: { Icon: AlertCircle, klass: 'text-destructive' },
    warn: { Icon: AlertTriangle, klass: 'text-warning' },
    info: { Icon: Info, klass: 'text-muted-foreground' },
  }[tier];
  const Icon = cfg.Icon;
  return (
    <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
      <Icon className={`h-3 w-3 mt-0.5 ${cfg.klass}`} />
      <span>{text}</span>
    </div>
  );
}
